import { type JSX } from 'react';

import { EloButton } from '@plurall/elo-v3';
import { useNavigate } from '@tanstack/react-router';

import { ENV } from '@/lib/constants/environment';

import {
  ErrorBoundaryActionsFactory,
  ErrorBoundaryContainerFactory,
  ErrorBoundaryDetailsFactory,
  ErrorBoundaryErrorBlockFactory,
  ErrorBoundaryErrorContentFactory,
  ErrorBoundaryMessageFactory,
  ErrorBoundaryRootFactory,
  ErrorBoundarySummaryFactory,
  ErrorBoundaryTitleFactory,
} from './errorBoundary.factory';
import { type ErrorFallbackProps } from './errorBoundary.schema';

export const ErrorFallback = ({
  error,
  errorInfo,
  onReset,
  resetButtonText = 'Tentar novamente',
}: ErrorFallbackProps): JSX.Element => {
  const navigate = useNavigate();
  const isDevelopment = ENV.DEV && import.meta.env.DEV;

  return (
    <ErrorBoundaryRootFactory role='alert'>
      <ErrorBoundaryContainerFactory>
        <ErrorBoundaryTitleFactory>Ops! Algo deu errado</ErrorBoundaryTitleFactory>

        <ErrorBoundaryMessageFactory>
          Ocorreu um erro inesperado. Por favor, tente novamente ou entre em contato com o suporte se o problema
          persistir.
        </ErrorBoundaryMessageFactory>

        {isDevelopment && error && (
          <ErrorBoundaryDetailsFactory>
            <ErrorBoundarySummaryFactory>Detalhes do erro (modo desenvolvimento)</ErrorBoundarySummaryFactory>
            <ErrorBoundaryErrorContentFactory>
              <ErrorBoundaryErrorBlockFactory>
                <strong>Erro:</strong>
                <pre>{error.toString()}</pre>
              </ErrorBoundaryErrorBlockFactory>

              {errorInfo && (
                <ErrorBoundaryErrorBlockFactory>
                  <strong>Stack trace:</strong>
                  <pre>{errorInfo.componentStack}</pre>
                </ErrorBoundaryErrorBlockFactory>
              )}
            </ErrorBoundaryErrorContentFactory>
          </ErrorBoundaryDetailsFactory>
        )}

        <ErrorBoundaryActionsFactory>
          <EloButton.Brand
            aria-label={resetButtonText}
            data-test-id='elo-brand-button'
            size='medium'
            onClick={onReset}>
            {resetButtonText}
          </EloButton.Brand>

          <EloButton.Neutral
            aria-label='Ir para página inicial'
            data-test-id='elo-neutral-button'
            size='medium'
            variant='outline'
            onClick={() => void navigate({ to: '/' })}>
            Ir para página inicial
          </EloButton.Neutral>
        </ErrorBoundaryActionsFactory>
      </ErrorBoundaryContainerFactory>
    </ErrorBoundaryRootFactory>
  );
};
